import type { SurveyQuestionDto, SurveySkipRuleDto } from '@/shared/api/types'

/**
 * The branch logic, client-side, matching `SkipRuleEvaluator` on the server.
 *
 * ## Why a second implementation exists at all
 *
 * Because the alternative is worse. The builder's preview has to answer "what
 * would a respondent see" BEFORE anything is submitted, and there is no endpoint
 * that answers that — nor should there be, since the answer changes on every
 * keystroke. So the preview evaluates locally.
 *
 * That makes this the one place in the feature where the same rule is expressed
 * twice, and the risk is exactly the one PollSphere fell into with its two
 * chi-square implementations. Three things keep it honest:
 *
 * 1. **This is a PREVIEW and never a decision.** Nothing here is sent to the
 *    server, and nothing the server stores depends on it. The submit path replays
 *    the server's own evaluator over what was actually answered — so if the two
 *    ever disagree, the server wins and the interview is still recorded
 *    correctly. The cost of a divergence is a designer being surprised, not a
 *    respondent's answer being lost.
 * 2. **The semantics are copied deliberately and commented at each branch**, in
 *    the same order the server applies them.
 * 3. **The preview only offers single-select answers**, so the multi-select
 *    subtleties (EQ against a set, exclusive options) never arise here and cannot
 *    drift.
 *
 * If a third caller ever needs this, the right move is a server endpoint that
 * takes a partial answer map and returns the hidden set — not a third copy.
 */

/** Question id → the option CODE the previewer picked. Codes, never ids: see below. */
export type AnswerMap = Record<string, string>

export interface SkipOutcome {
  /** Question id → the rule that hid it. Absent means shown. */
  hiddenBy: Record<string, string>
  /** True when an END_INTERVIEW rule fired. */
  ended: boolean
}

export function evaluateSkips(
  questions: SurveyQuestionDto[],
  rules: SurveySkipRuleDto[],
  answers: AnswerMap,
): SkipOutcome {
  const ordered = [...questions].sort((a, b) => a.position - b.position)
  const byId = new Map(ordered.map((q) => [q.id, q]))
  const hiddenBy: Record<string, string> = {}
  let ended = false

  for (const rule of [...rules].sort((a, b) => a.position - b.position)) {
    const fires = matches(rule, answers[rule.sourceQuestionId] ?? null)
    const source = byId.get(rule.sourceQuestionId)

    switch (rule.actionCode) {
      case 'HIDE':
        if (fires) targets(rule, ordered).forEach((id) => { hiddenBy[id] = rule.id })
        break
      case 'SHOW':
        // The inverse: the target is conditional and stays hidden while the
        // condition is false, so an author can write "only ask this if" without
        // also writing the negation as a HIDE.
        if (fires) targets(rule, ordered).forEach((id) => { delete hiddenBy[id] })
        else targets(rule, ordered).forEach((id) => { hiddenBy[id] = rule.id })
        break
      case 'SKIP_TO': {
        if (!fires || !source) break
        const firstTarget = targets(rule, ordered)[0]
        const target = firstTarget ? byId.get(firstTarget) : undefined
        const to = target ? target.position : Number.MAX_SAFE_INTEGER
        // Everything STRICTLY BETWEEN. The target is where the interview
        // resumes, so it is not hidden.
        ordered
          .filter((q) => q.position > source.position && q.position < to)
          .forEach((q) => { hiddenBy[q.id] = rule.id })
        break
      }
      case 'END_INTERVIEW':
        if (fires && source) {
          ordered.filter((q) => q.position > source.position)
            .forEach((q) => { hiddenBy[q.id] = rule.id })
          ended = true
        }
        break
      default:
        // A rule this build cannot evaluate is INERT rather than guessed at.
        // Firing on an unrecognised action would hide questions nobody meant to.
        break
    }
  }
  return { hiddenBy, ended }
}

function targets(rule: SurveySkipRuleDto, ordered: SurveyQuestionDto[]): string[] {
  if (rule.targetQuestionId) return [rule.targetQuestionId]
  if (!rule.targetBlockId) return []
  return ordered.filter((q) => q.blockId === rule.targetBlockId).map((q) => q.id)
}

/**
 * Does the rule's condition hold?
 *
 * Compares on the option CODE, exactly as the server does. Not on the option id —
 * those are per-version and copy-on-write gives a new one — and not on the label,
 * which is editable, so relabelling would silently change which branch fires.
 *
 * A `value_json` that will not parse does NOT fire. Firing on unparseable
 * configuration would hide questions nobody meant to hide, and the previewer
 * would have no way to tell.
 */
function matches(rule: SurveySkipRuleDto, answer: string | null): boolean {
  if (rule.operatorCode === 'ANSWERED') return answer !== null
  if (rule.operatorCode === 'NOT_ANSWERED') return answer === null

  let value: unknown
  try {
    value = rule.valueJson ? JSON.parse(rule.valueJson) : null
  } catch {
    return false
  }
  if (value === null || value === undefined) return false

  const list = Array.isArray(value) ? value : [value]
  const same = (candidate: unknown) =>
    answer !== null && String(candidate).toUpperCase() === answer.toUpperCase()

  switch (rule.operatorCode) {
    case 'EQ': return list.length === 1 ? same(list[0]) : list.every(same)
    case 'NEQ': return !(list.length === 1 ? same(list[0]) : list.every(same))
    case 'IN': return list.some(same)
    case 'NOT_IN': return answer !== null && !list.some(same)
    // GT and LT are numeric, and the preview only offers option answers, so they
    // never hold here. The server evaluates them against the real numeric value.
    case 'GT': case 'LT': return false
    default: return false
  }
}
